import { RefreshCwIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { createServerPairingCredential } from "~/environments/primary";
import { resolveMobilePairingUrl } from "~/pairingUrl";
import { normalizeExpiresAt, resolvePairingUrl } from "~/lib/pairingPresentation";
import { Button } from "../ui/button";
import { QRCodeSvg } from "../ui/qr-code";
import { Spinner } from "../ui/spinner";
import { toastManager } from "../ui/toast";

type PairingPageState =
  | { readonly status: "loading" }
  | {
      readonly status: "ready";
      readonly expiresAt: string;
      readonly mobilePairingUrl: string;
      readonly pairingUrl: string;
    }
  | { readonly status: "error"; readonly message: string };

export function ForgeMobilePairingPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<PairingPageState>({ status: "loading" });

  const createPairingCode = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const credential = await createServerPairingCredential("Forge Mobile", "owner");
      const pairingUrl = await resolvePairingUrl(credential.credential);
      setState({
        status: "ready",
        expiresAt: normalizeExpiresAt(credential.expiresAt),
        mobilePairingUrl: resolveMobilePairingUrl(pairingUrl),
        pairingUrl,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not create a pairing code.";
      setState({ status: "error", message });
      toastManager.add({
        type: "error",
        title: "Could not create pairing code",
        description: message,
      });
    }
  }, []);

  useEffect(() => {
    void createPairingCode();
  }, [createPairingCode]);

  const handleManageDevices = useCallback(() => {
    void navigate({ to: "/settings/devices" });
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    void createPairingCode();
  }, [createPairingCode]);

  const expiresLabel =
    state.status === "ready"
      ? new Intl.DateTimeFormat(undefined, {
          hour: "numeric",
          minute: "2-digit",
        }).format(new Date(state.expiresAt))
      : null;

  return (
    <main className="flex min-h-full flex-1 items-center justify-center bg-background px-6 py-12 text-foreground">
      <section className="flex w-full max-w-[320px] flex-col items-center text-center">
        <h1 className="text-[22px] font-semibold leading-7 tracking-tight">Forge Mobile</h1>
        <p className="mt-2 max-w-[280px] text-[13px] leading-5 text-muted-foreground">
          Scan this code with your phone to connect it to this Forge session.
        </p>

        <div className="mt-8 flex min-h-[252px] w-full items-center justify-center">
          {state.status === "loading" ? (
            <div className="flex min-h-[252px] w-[252px] flex-col items-center justify-center gap-3 rounded-[10px] border border-border/70 bg-card text-sm text-muted-foreground">
              <Spinner className="size-5" />
              <span>Creating pairing code...</span>
            </div>
          ) : state.status === "error" ? (
            <div className="flex min-h-[252px] w-[252px] flex-col items-center justify-center gap-4 rounded-[10px] border border-border/70 bg-card px-6 text-sm">
              <p className="max-w-xs text-muted-foreground">{state.message}</p>
              <Button type="button" size="sm" onClick={handleRefresh}>
                Try again
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="rounded-[10px] border border-border/70 bg-card p-3.5 shadow-sm">
                <QRCodeSvg
                  value={state.mobilePairingUrl}
                  size={220}
                  level="M"
                  marginSize={2}
                  title="Forge Mobile pairing QR code"
                />
              </div>
              <div className="mt-3 flex min-h-7 items-center gap-2">
                <p className="text-xs text-muted-foreground">
                  {expiresLabel ? `Expires at ${expiresLabel}` : "Pairing code ready"}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Refresh pairing code"
                  onClick={handleRefresh}
                  className="size-7"
                >
                  <RefreshCwIcon aria-hidden="true" className="size-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {state.status === "ready" ? (
          <p className="sr-only">Pairing URL: {state.pairingUrl}</p>
        ) : null}
        <button
          type="button"
          className="mt-3 rounded-md px-2 py-1 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onClick={handleManageDevices}
        >
          All devices
        </button>
      </section>
    </main>
  );
}
